import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import momentJalaali from 'moment-jalaali';
import axios from 'src/app/Service/axios';
import {
  Permissions,
  RawRepair,
  RepairFilters,
  RepairRow,
  RepairType,
  ServiceItem,
  ServiceStatus,
  StatusDisplay,
} from './repairs.types';
import {
  DEFAULT_PAGE_SIZE,
  formatWithCommas,
  groupDigits,
  isBlank,
  PAGE_SIZE_OPTIONS,
  parseResponse,
  SERVICE_STATUS_DISPLAY,
  stripCommas,
  toEnglishDigits,
  toRepairRow,
} from './repairs.utils';

declare let $: any;

// ---------- کامپوننت ----------
@Component({
  selector: 'Repairs',
  templateUrl: './repairs.component.html',
})
export class RepairsComponent implements OnInit {
  readonly loggedInUser = this.gurdService.decrypt(localStorage.getItem('FullName')!);

  readonly vehicle = {
    ownerName: this.gurdService.decrypt(localStorage.getItem('carName')!),
    plate: this.gurdService.decrypt(localStorage.getItem('plate')!).split(',').join(' '),
    hoze: this.gurdService.decrypt(localStorage.getItem('hze')!),
  };

  readonly reversedPlate = this.otherService.returnCorrectPlate(this.vehicle.plate);
  readonly currentDate = momentJalaali().locale('fa').format('jYYYY/jMM/jDD');

  // ----- دسترسی‌ها و شناسه‌ها -----
  permissions: Partial<Permissions> = {};
  unitId = '';
  lastKm = 0;
  lastKmText = groupDigits(0);

  repairs: RepairRow[] = [];

  filteredRows: RepairRow[] = [];

  pagedRows: RepairRow[] = [];

  totalPages = 1;

  filteredTotalCost = 0;
  filteredTotalText = groupDigits(0);

  searchText = '';
  currentPage = 1;
  pageSize = DEFAULT_PAGE_SIZE;
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  private activeFilters: RepairFilters = {};

  // ----- فیلترها -----
  filtersOpen = false;
  activePeriod = '';

  // ----- وضعیت UI -----
  loading = false;
  hasError = false;
  isEditMode = false;
  isExporting = false;

  tooltip = { text: '', visible: false, x: 0, y: 0 };

  // ----- سرویس‌ها داخل مودال تعمیرات -----
  repairTypes: RepairType[] = [];
  serviceItems: ServiceItem[] = [];

  readonly ServiceStatus = ServiceStatus;
  readonly groupDigits = groupDigits;

  // ----- فرم‌ها -----
  filterForm: FormGroup = this.formBuilder.group({
    fromDate: new FormControl(''),
    toDate: new FormControl(''),
    reason: new FormControl(''),
    repairer: new FormControl(''),
  });

  repairForm: FormGroup = this.formBuilder.group({
    iSystemUser: this.gurdService.decrypt(localStorage.getItem('ID')!),
    strSession: this.gurdService.decrypt(localStorage.getItem('Session')!),
    strUnitId: new FormControl(''),
    strDate: new FormControl(''),
    strReason: new FormControl(''),
    strRepairman: new FormControl(''),
    strComment: new FormControl(''),
    iKM: new FormControl(''),
    iTotalCost: new FormControl(''),
    iRepairMaintenanceRecord: new FormControl(''),
    strImage: new FormControl(''),
  });

  constructor(
    private notifyService: NotificationService,
    private router: Router,
    public otherService: OtherService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private location: Location,
    private gurdService: GurdServiceService,
    private cdr: ChangeDetectorRef,
  ) {}

  // ================= چرخه‌ی حیات =================
  ngOnInit(): void {
    this.permissions = JSON.parse(this.gurdService.decrypt(localStorage.getItem('menu')!));

    this.route.params.subscribe((params) => {
      this.unitId = params['id'];
      this.loadLastKm();
    });

    this.loadRepairs();
    this.loadRepairTypes();
  }

  goBack(): void {
    this.location.back();
  }

  // ================= بارگذاری =================
  async loadRepairs(filters: RepairFilters = {}): Promise<void> {
    this.activeFilters = filters;
    this.loading = true;
    this.hasError = false;

    try {
      const query = [
        `strUnitID=${this.unitId}`,
        `strSDate=${encodeURIComponent(filters.fromDate ?? '')}`,
        `strEDate=${encodeURIComponent(filters.toDate ?? '')}`,
        `strRepairman=${encodeURIComponent(filters.repairer ?? '')}`,
        `strReason=${encodeURIComponent(filters.reason ?? '')}`,
        this.authQuery(),
      ].join('&');

      const resp = await axios.post(`GetRepairMaintenanceRecordNew?${query}`);
      const data: RawRepair[] = resp.data ?? [];

      if (data[0]?.ISSessionExpired == true) {
        this.expireSession();
        return;
      }

      // for (let index = 0; index < 10000; index++) {
      //   (this.repairs as any).push(...data)
      // }
      // this.repairs = this.repairs.map((raw) => toRepairRow(raw));

      this.repairs = data.map((raw) => toRepairRow(raw));
    } catch {
      this.hasError = true;
      this.repairs = [];
    } finally {
      this.loading = false;
      this.refreshTable();
    }
  }

  private async loadLastKm(): Promise<void> {
    const model = {
      ID: this.gurdService.decrypt(localStorage.getItem('ID')!),
      strSession: this.gurdService.decrypt(localStorage.getItem('Session')!),
    };

    const resp = await axios.post(`GetCar?strUnitID=${this.unitId}`, model);

    if (resp.data?.ISSessionExpired == true) {
      this.expireSession();
      return;
    }

    if (resp.data == null) {
      this.notifyService.showError('بروز خطا در سامانه');
      return;
    }

    this.lastKm = resp.data.fLastKM;
    this.lastKmText = groupDigits(this.lastKm);
  }

  private async loadRepairTypes(): Promise<void> {
    const model = {
      iSystemUser: this.gurdService.decrypt(localStorage.getItem('ID')!),
      strSession: this.gurdService.decrypt(localStorage.getItem('Session')!),
    };

    const resp = await axios.post(`RepairMaintenanceType`, model);

    if (resp.data?.ISSessionExpired == true) {
      this.expireSession();
      return;
    }

    if (resp.data == null) {
      this.notifyService.showError('بروز خطا در سامانه');
      return;
    }

    this.repairTypes = resp.data;
  }

  // ================= جدول، جستجو و صفحه‌بندی =================
  private refreshTable(resetPage = false): void {
    if (resetPage) this.currentPage = 1;

    const query = toEnglishDigits(this.searchText).trim();

    this.filteredRows = query
      ? this.repairs.filter((row) => row.searchIndex.includes(query))
      : this.repairs;

    let total = 0;
    for (const row of this.filteredRows) total += row.cost;
    this.filteredTotalCost = total;
    this.filteredTotalText = groupDigits(total);

    this.totalPages = Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    this.currentPage = Math.min(this.currentPage, this.totalPages);

    const start = (this.currentPage - 1) * this.pageSize;
    this.pagedRows = this.filteredRows.slice(start, start + this.pageSize);
  }

  onSearchChange(): void {
    this.refreshTable(true);
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.refreshTable(true);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.refreshTable();
    this.hideTooltip();
  }

  trackById(_index: number, row: RepairRow): number {
    return row.iRepairMaintenanceRecord;
  }

  // ================= فیلترها =================
  toggleFilters(): void {
    this.filtersOpen = !this.filtersOpen;
  }

  openFilters(): void {
    this.filtersOpen = true;
  }

  get filterCount(): number {
    const v = this.filterForm.value;
    return [v.fromDate, v.toDate, v.reason, v.repairer].filter(Boolean).length;
  }

  get hasActiveFilters(): boolean {
    return this.filterCount > 0 || !!this.searchText.trim();
  }

  setPeriod(period: string): void {
    this.activePeriod = period;
    this.filterForm.patchValue({
      fromDate: period
        ? momentJalaali().subtract(Number(period), 'days').format('jYYYY/jMM/jDD')
        : '',
      toDate: '',
    });
  }

  applyFilters(): void {
    const { fromDate, toDate, repairer, reason } = this.filterForm.value;
    this.currentPage = 1;
    this.loadRepairs({ fromDate, toDate, repairer, reason });
    this.filtersOpen = false;
  }

  resetFilters(): void {
    this.filterForm.reset({ fromDate: '', toDate: '', reason: '', repairer: '' });
    this.searchText = '';
    this.activePeriod = '';
    this.currentPage = 1;
    this.loadRepairs();
  }

  // ================= فرم ثبت / ویرایش =================
  startCreate(): void {
    this.isEditMode = false;
    this.serviceItems = [];

    this.repairForm.patchValue({
      strDate: momentJalaali().format('jYYYY/jMM/jDD'),
      strReason: '',
      strRepairman: '',
      strComment: '',
      iKM: this.lastKm,
      iTotalCost: '',
      iRepairMaintenanceRecord: '',
      strImage: '',
    });
  }

  openEditModal(row: RepairRow): void {
    this.isEditMode = true;
    this.serviceItems = [];

    this.repairForm.patchValue({
      strDate: row.strDate,
      strReason: row.strReason,
      strComment: row.strComment,
      strImage: row.strImage,
      strRepairman: row.strRepairman,
      iKM: row.iKM,
      iTotalCost: formatWithCommas(row.iTotalCost),
      iRepairMaintenanceRecord: row.iRepairMaintenanceRecord,
    });

    void this.ensureServiceItems();
    $('#exampleModal').modal('show');
  }

  onNumberKeyPress(event: KeyboardEvent): boolean {
    if (isNaN(event.key as any)) return false;
    this.otherService.insertComma(event);
    return true;
  }

  // ================= ثبت / ویرایش / حذف =================
  async saveRepair(): Promise<void> {
    const payload = this.buildPayload();
    if (!this.isValidPayload(payload)) return;
    if (!confirm('آیا از ثبت اطلاعات اطمینان دارید؟')) return;

    await this.submitRecord('RepairMaintenanceRecordNew_Save', payload, {
      successMessage: 'با موفقیت ثبت شد',
      expiredCode: 1,
      resetPage: true,
    });
  }

  async updateRepair(): Promise<void> {
    const payload = this.buildPayload();
    if (!this.isValidPayload(payload)) return;
    if (!confirm('آیا از ویرایش اطلاعات اطمینان دارید؟')) return;

    await this.submitRecord('RepairMaintenanceRecordNew_Update', payload, {
      successMessage: 'با موفقیت ویرایش شد',
      expiredCode: 2,
    });
  }

  async confirmDelete(row: RepairRow): Promise<void> {
    if (!confirm('آیا از حذف اطلاعات اطمینان دارید؟')) return;

    const id = row.iRepairMaintenanceRecord;
    const resp = await axios.post(
      `DeleteRepairMaintenanceRecord?iRepairMaintenanceRecord=${id}&${this.authQuery()}`,
    );

    if (resp.data == -100) {
      this.expireSession(3);
      return;
    }

    if (resp.data == 1) {
      this.notifyService.showSuccess('با موفقیت حذف شد');
      this.repairs = this.repairs.filter((r) => r.iRepairMaintenanceRecord !== id);
      this.refreshTable();
      return;
    }

    this.notifyService.showError('خطایی رخ داده');
  }

  private async submitRecord( endpoint: string, payload: Record<string, any>,
    options: { successMessage: string; expiredCode: number; resetPage?: boolean },
  ): Promise<void> {
    const resp = await axios.post(endpoint, payload);

    if (resp.data == -100) {
      this.expireSession(options.expiredCode);
      return;
    }

    const result = parseResponse(resp.data);

    if (result?.iResult == 1) {
      this.notifyService.showSuccess(options.successMessage);
      this.isEditMode = false;
      if (options.resetPage) this.currentPage = 1;
      this.loadRepairs(this.activeFilters);
      $('#exampleModal').modal('hide');
      return;
    }

    this.notifyService.showError(result?.str ?? 'خطایی رخ داده');
  }

  private buildPayload(): Record<string, any> {
    const form = this.repairForm.value;

    return {
      ...form,
      strUnitId: this.unitId,
      iTotalCost: isBlank(form.iTotalCost)
        ? form.iTotalCost
        : parseFloat(String(form.iTotalCost).replace(/,/g, '')),
      lstmdlRepairMaintenance_Save: this.serviceItems.map((item) => ({
        ...item,
        iCost: stripCommas(item.iCost),
      })),
    };
  }

  private isValidPayload(payload: Record<string, any>): boolean {
    if (isBlank(payload['iTotalCost']) && isBlank(payload['strReason'])) {
      this.notifyService.showError('لطفا مقدار هزینه یا موضوع را وارد کنید');
      return false;
    }
    return true;
  }

  // ================= سرویس‌ها و اقدامات =================
  async openServiceTypes(): Promise<void> {
    $('#exampleModal').modal('hide');
    await this.ensureServiceItems();
    $('#serviceType_Modal').modal('show');
  }

  backToRepairForm(): void {
    $('#exampleModal').modal('show');
    $('#serviceType_Modal').modal('hide');
  }

  setServiceStatus(index: number, status: ServiceStatus): void {
    this.serviceItems[index].tiStatus = status;
  }

  statusDisplay(status: number): StatusDisplay {
    return SERVICE_STATUS_DISPLAY[status] ?? SERVICE_STATUS_DISPLAY[ServiceStatus.None];
  }

  private async ensureServiceItems(): Promise<void> {
    if (this.serviceItems.length) return;

    if (!this.isEditMode) {
      this.serviceItems = this.defaultServiceItems();
      return;
    }

    this.otherService.Showspinner();
    try {
      const resp = await axios.post(`RepairMaintenance_List`, this.repairForm.value);
      const saved: any[] = parseResponse(resp.data) ?? [];

      this.serviceItems = saved.length
        ? saved.map((a) => ({
            iRepairMaintenanceType: a.iRepairMaintenanceType,
            strTitle: a.strRepairMaintenanceType_strComment,
            iCost: a.iCost == 0 ? '' : a.iCost,
            strComment: a.strComment ?? '',
            tiStatus: a.tiStatus,
            bExpanded: a.iCost != 0 || (a.strComment ?? '').trim() !== '',
          }))
        : this.defaultServiceItems();
    } finally {
      this.otherService.nonespinner();
    }
  }

  private defaultServiceItems(): ServiceItem[] {
    return this.repairTypes.map((t) => ({
      iRepairMaintenanceType: t.iRepairMaintenanceType,
      strTitle: t.strComment,
      iCost: '',
      strComment: '',
      tiStatus: ServiceStatus.None,
      bExpanded: false,
    }));
  }

  // ================= تصویر فاکتور =================
  onImageSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension !== 'jpg' && extension !== 'jpeg') {
      this.notifyService.showError('فرمت قابل قبول فقط jpg است');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result).replace('data:image/jpeg;base64,', '');
      this.repairForm.patchValue({ strImage: base64 });
    };
    reader.readAsDataURL(file);
  }

  toggleImageFullScreen(): void {
    document.getElementById('fullScreen_')?.classList.toggle('full-screen');
  }

  // ================= چاپ و اکسل =================
  printReport(): void {
    this.renderExportTable(() => this.openPrintWindow('print-section', 'سوابق سرویس و تعمیرات'));
  }

  exportExcel(): void {
    this.renderExportTable(() =>
      this.otherService.exportexcel('excel-table-repairsRep', 'سوابق سرویس و تعمیرات.xlsx'),
    );
  }

  private renderExportTable(action: () => void): void {
    this.isExporting = true;
    this.cdr.detectChanges();
    try {
      action();
    } finally {
      this.isExporting = false;
      this.cdr.detectChanges();
    }
  }

  private openPrintWindow(sectionId: string, title: string): void {
    const section = document.getElementById(sectionId);
    if (!section) return;

    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map((el) => el.outerHTML)
      .join('\n');

    const content = section.outerHTML.replace('print-only', '');

    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) {
      this.notifyService.showError('مرورگر پنجره‌ی چاپ را مسدود کرده است');
      return;
    }

    win.document.open();
    win.document.write(`
      <!doctype html>
      <html dir="rtl" lang="fa">
        <head>
          <meta charset="utf-8" />
          <title>${title}</title>
          ${styles}
        </head>
        <body>${content}</body>
      </html>
    `);
    win.document.close();

    setTimeout(() => {
      win.focus();
      win.print();
      win.close();
    }, 300);
  }

  // ================= تولتیپ و کلیدها =================
  showTooltip(event: MouseEvent, text: string): void {
    if (!text) return;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.tooltip = {
      text,
      visible: true,
      x: rect.left + rect.width / 2,
      y: rect.top,
    };
  }

  hideTooltip(): void {
    if (this.tooltip.visible) this.tooltip.visible = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.hideTooltip();
  }

  // ================= کمکی‌ها =================
  private authQuery(): string {
    const session = encodeURIComponent(
      this.gurdService.decrypt(localStorage.getItem('Session')!),
    );
    const userId = this.gurdService.decrypt(localStorage.getItem('ID')!);
    return `iSystemUser=${userId}&strSession=${session}`;
  }

  private expireSession(errCode?: number): void {
    this.notifyService.showError('لطفا وارد سیستم شوید');
    this.otherService.removeLocalStorage();
    this.router.navigate(['/login'], errCode ? { queryParams: { ERR_CODE: errCode } } : {});
  }
}
